import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { CalendarToday, Settings, Analytics, Schedule, Notifications } from '@mui/icons-material';

import SchedulingConfiguration from './SchedulingConfiguration';
import MeetingCalendar from './MeetingCalendar';
import MeetingList from './MeetingList';
import SchedulingAnalytics from './SchedulingAnalytics';
import NotificationAnalytics from './NotificationAnalytics';
import { schedulingService } from '../../services/schedulingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scheduling-tabpanel-${index}`}
      aria-labelledby={`scheduling-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Scheduling: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const summary = await schedulingService.getDashboardSummary();
      setDashboardSummary(summary);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box className="fade-in" display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="fade-in">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Gestión de Citas
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Administra las citas y reuniones programadas con clientes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {dashboardSummary && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Resumen de Hoy
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {dashboardSummary.today_meetings_count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Citas programadas hoy
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {dashboardSummary.week_analytics?.completion_rate?.toFixed(1) || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tasa de completación semanal
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {dashboardSummary.week_analytics?.total_bookings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total reservas esta semana
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="scheduling tabs">
            <Tab
              icon={<CalendarToday />}
              label="Calendario"
              id="scheduling-tab-0"
              aria-controls="scheduling-tabpanel-0"
            />
            <Tab
              icon={<Schedule />}
              label="Lista de Citas"
              id="scheduling-tab-1"
              aria-controls="scheduling-tabpanel-1"
            />
            <Tab
              icon={<Settings />}
              label="Configuración"
              id="scheduling-tab-2"
              aria-controls="scheduling-tabpanel-2"
            />
            <Tab
              icon={<Analytics />}
              label="Analíticas"
              id="scheduling-tab-3"
              aria-controls="scheduling-tabpanel-3"
            />
            <Tab
              icon={<Notifications />}
              label="Notificaciones"
              id="scheduling-tab-4"
              aria-controls="scheduling-tabpanel-4"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <MeetingCalendar />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <MeetingList />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <SchedulingConfiguration onConfigurationChange={loadDashboardData} />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <SchedulingAnalytics />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <NotificationAnalytics />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Scheduling;
